import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  Download,
  FileText,
  Paperclip,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  type OwnerPayApp,
  useBillingPeriods,
  useCreateOwnerPayApp,
  useDeleteOwnerPayApp,
  useOwnerContracts,
  useOwnerPayApps,
} from "@/hooks/use-pay-applications";

type SortKey = keyof Pick<
  OwnerPayApp,
  | "applicationNo"
  | "projectName"
  | "periodEnd"
  | "status"
  | "currentPaymentDue"
  | "retainageAmount"
  | "totalEarnedLessRetainage"
>;

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  const s = status?.toUpperCase();
  switch (s) {
    case "APPROVED":
    case "CERTIFIED":
    case "PAID":
      return "success";
    case "DRAFT":
    case "SUBMITTED":
      return "warning";
    case "REJECTED":
      return "destructive";
    default:
      return "default";
  }
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "--";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function sortValue(pa: OwnerPayApp, key: SortKey): string | number {
  const v = pa[key];
  if (v === null || v === undefined) return "";
  if (key === "periodEnd") return v ? new Date(v as string).getTime() : 0;
  return v as string | number;
}

function exportCsv(rows: OwnerPayApp[], scoped: boolean) {
  const headers = [
    "App #",
    ...(scoped ? [] : ["Project"]),
    "Period To",
    "Status",
    "Amount Due",
    "Retainage",
    "Net Payment",
    "Files",
  ];
  const lines = rows.map((r) =>
    [
      r.applicationNo ?? "",
      ...(scoped ? [] : [r.projectName ?? ""]),
      formatDate(r.periodEnd),
      r.status ?? "",
      r.currentPaymentDue,
      r.retainageAmount,
      r.totalEarnedLessRetainage,
      r.attachmentCount,
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "owner-pay-apps.csv";
  a.click();
  URL.revokeObjectURL(url);
}

interface OwnerPayAppsListProps {
  /** When set, scopes the list to a single project and hides project filters/columns. */
  projectId?: string;
  /** Heading text. Defaults to "Owner Pay Applications". Pass empty string to hide. */
  title?: string;
}

export function OwnerPayAppsList({
  projectId,
  title = "Owner Pay Applications",
}: OwnerPayAppsListProps) {
  const scoped = Boolean(projectId);
  const { data, isLoading, isError, error, refetch } = useOwnerPayApps(
    projectId ? { projectId } : undefined,
  );
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [ownerContractId, setOwnerContractId] = useState("");
  const [billingPeriodId, setBillingPeriodId] = useState("");
  const [description, setDescription] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<OwnerPayApp | null>(null);

  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("applicationNo");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<SortKey | null>(null);

  const { data: ownerContracts } = useOwnerContracts(projectId ? { projectId } : undefined);
  const { data: billingPeriods } = useBillingPeriods();
  const createMutation = useCreateOwnerPayApp();
  const deleteMutation = useDeleteOwnerPayApp();

  const allItems = data ?? [];

  const projectOptions = useMemo(() => {
    const names = [...new Set(allItems.map((pa) => pa.projectName).filter(Boolean))].sort();
    return names as string[];
  }, [allItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((pa) => {
      if (!scoped) {
        const matchesProject = projectFilter === "all" || pa.projectName === projectFilter;
        if (!matchesProject) return false;
      }
      if (!q) return true;
      return [
        String(pa.applicationNo ?? ""),
        pa.projectName ?? "",
        formatDate(pa.periodEnd),
        pa.status ?? "",
        formatCurrency(pa.currentPaymentDue),
        formatCurrency(pa.retainageAmount),
        formatCurrency(pa.totalEarnedLessRetainage),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [allItems, search, projectFilter, scoped]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortedColumn === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(key);
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortedColumn !== col) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  }

  function handleCreate() {
    if (!ownerContractId) return;
    const contract = (ownerContracts ?? []).find((c) => c.id === ownerContractId);
    if (!contract) return;
    const existingCount = allItems.filter((pa) => pa.commitmentId === ownerContractId).length;
    createMutation.mutate(
      {
        projectId: contract.projectId,
        commitmentId: ownerContractId,
        billingPeriodId: billingPeriodId || undefined,
        applicationNo: existingCount + 1,
        description: description || undefined,
      },
      {
        onSuccess: (res) => {
          setDialogOpen(false);
          setOwnerContractId("");
          setBillingPeriodId("");
          setDescription("");
          const newId = res?.data?.id;
          if (newId) {
            navigate({ to: "/pay-applications/owner/$id", params: { id: newId } });
          }
        },
      },
    );
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load owner pay applications"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        {title ? <h1 className="text-2xl font-bold">{title}</h1> : <div />}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Owner Pay App
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Owner Pay Application</DialogTitle>
              <DialogDescription>
                Create a new pay application from general contractor to owner.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Owner Contract</Label>
                <Select value={ownerContractId} onValueChange={setOwnerContractId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select owner contract" />
                  </SelectTrigger>
                  <SelectContent>
                    {(ownerContracts ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.projectName ?? c.title} — {formatCurrency(c.contractSumToDate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Billing Period</Label>
                <Select value={billingPeriodId} onValueChange={setBillingPeriodId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select billing period" />
                  </SelectTrigger>
                  <SelectContent>
                    {(billingPeriods ?? []).map((bp) => (
                      <SelectItem key={bp.id} value={bp.id}>
                        {bp.periodStart} to {bp.periodEnd} ({bp.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!ownerContractId || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create Pay App"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters + Search + Export */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search pay apps..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        {scoped ? null : (
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projectOptions.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportCsv(sorted, scoped)}
          disabled={sorted.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>
              {allItems.length === 0
                ? "No owner pay applications found."
                : "No results match your search."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("applicationNo")}
                >
                  App #
                  <SortIcon col="applicationNo" />
                </TableHead>
                {scoped ? null : (
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("projectName")}
                  >
                    Project
                    <SortIcon col="projectName" />
                  </TableHead>
                )}
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("periodEnd")}
                >
                  Period To
                  <SortIcon col="periodEnd" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("status")}
                >
                  Status
                  <SortIcon col="status" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("currentPaymentDue")}
                >
                  Amount Due
                  <SortIcon col="currentPaymentDue" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("retainageAmount")}
                >
                  Retainage
                  <SortIcon col="retainageAmount" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("totalEarnedLessRetainage")}
                >
                  Net Payment
                  <SortIcon col="totalEarnedLessRetainage" />
                </TableHead>
                <TableHead className="text-center">Files</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((payApp) => (
                <TableRow
                  key={payApp.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/pay-applications/owner/$id",
                      params: { id: payApp.id },
                    })
                  }
                >
                  <TableCell className="font-medium">{payApp.applicationNo ?? "--"}</TableCell>
                  {scoped ? null : <TableCell>{payApp.projectName || "--"}</TableCell>}
                  <TableCell>{formatDate(payApp.periodEnd)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(payApp.status)}>
                      {formatStatusLabel(payApp.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(payApp.currentPaymentDue)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(payApp.retainageAmount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(payApp.totalEarnedLessRetainage)}
                  </TableCell>
                  <TableCell className="text-center">
                    {payApp.attachmentCount > 0 ? (
                      <span className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Paperclip className="h-3 w-3" />
                        {payApp.attachmentCount}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {payApp.status === "DRAFT" ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(payApp);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Owner Pay App</AlertDialogTitle>
            <AlertDialogDescription>
              Delete Pay App #{deleteTarget?.applicationNo} for{" "}
              {deleteTarget?.projectName ?? "this project"}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
